import type {
  AssistedAffiliationFiles,
  AssistedAffiliationFieldName,
  AssistedAffiliationForm,
  LegalDocument,
  LegalDocumentFiles,
  SelectOption,
} from "./types";
import countries from "@/data/countries.json";
import { isAfter, isBefore, isSameDay } from 'date-fns';
import type { CustomTableColumn } from '@/components/Form/Table/CustomTable';
import type {
  AssistedAffiliationFilters,
  AssistedAffiliationStatus,
  AssistedAffiliationValidations,
  SearchKey,
  ValidationChange,
  ValidationSource,
} from './types';


const toOptions = (values: string[]): SelectOption[] =>
  values.map((value) => ({ label: value, value }));

type CountryOptionItem = {
  country_id: string;
  value: string;
};

const removeFlag = (value: string) =>
  value.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, "").trim();

const mapCountriesToNationalityOptions = (
  countryList: CountryOptionItem[]
): SelectOption[] =>
  [...countryList]
    .sort((first, second) =>
      removeFlag(first.value).localeCompare(removeFlag(second.value), "es")
    )
    .map((country) => ({
      label: `${country.country_id} (${removeFlag(country.value)})`,
      value: country.country_id,
    }));


export const STATUS_LABELS: Record<AssistedAffiliationStatus, string> = {
  DRAFT: "Borrador",
  IN_PROGRESS: "En proceso",
  PENDING_INFORMATION: "Información pendiente",
  COMMERCE_CREATED: "Comercio creado",
  REQUEST_CANCELLED: "Rechazado",
  CREATION_ERROR: "Error de creación",
};

export const EDITABLE_STATUSES: AssistedAffiliationStatus[] = [
  "DRAFT",
  "PENDING_INFORMATION",
];

const dateFormatter = new Intl.DateTimeFormat("es-DO", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

export const formatCreatedAt = (value: string | number): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return dateFormatter.format(date).replace(",", "").replace(/\u00A0/g, " ");
};

export const COLUMNS: CustomTableColumn[] = [
  { id: "registryNumber", label: "Registro #" },
  { id: "createdAt", label: "Fecha de creación", format: formatCreatedAt },
  { id: "identification", label: "Identificación" },
  { id: "commercialName", label: "Nombre comercial" },
  { id: "legalRepresentative", label: "Representante legal" },
  { id: "status", label: "Estado" },
  { id: "bhdExecutive", label: "Ejecutivo BHD" },
  { id: "commercialAssignee", label: "Responsable comercial" },
  { id: "actions", label: "Acciones" },
];

export const SEARCH_KEY_LABELS: Record<SearchKey, string> = {
  identification: "Identificación",
  commercialName: "Nombre del comercio",
  legalRepresentative: "Representante legal",
  bhdExecutive: "Ejecutivo BHD",
  commercialAssignee: "Responsable comercial",
};

export const DEFAULT_SEARCH_KEY: SearchKey = "identification";

// Clasifica un día del calendario respecto al rango Desde/Hasta para pintarlo como en el
// date-picker de la app de comercios: extremos en círculo verde y banda clara entre ambos.
export const classifyRangeDay = (
  day: Date,
  dateFrom: Date | null,
  dateTo: Date | null,
): 'endpoint' | 'band' | null => {
  if ((dateFrom && isSameDay(day, dateFrom)) || (dateTo && isSameDay(day, dateTo))) {
    return 'endpoint';
  }
  if (dateFrom && dateTo && isAfter(day, dateFrom) && isBefore(day, dateTo)) {
    return 'band';
  }
  return null;
};

// Estado inicial de la bandeja y destino del botón "Limpiar filtros".
export const EMPTY_FILTERS: AssistedAffiliationFilters = {
  searchKey: DEFAULT_SEARCH_KEY,
  searchTerm: "",
  dateFrom: null,
  dateTo: null,
};

export const EMPTY_TABLE_MESSAGE = "No hay registro(s)";

export const LIST_LOAD_ERROR_MESSAGE = "No se pudo cargar el listado de solicitudes.";

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// El bloque que se pide al servidor debe cubrir DOS páginas del mayor tamaño elegible. Con una
// sola no alcanza: el bloque siguiente se pide justo cuando el usuario ya entró a la página que no
// tiene filas, así que la tabla queda en blanco mientras viaja la consulta. El pie no lo disimula
// porque cuenta con el total del servidor, no con lo que hay cargado.
export const REQUESTS_CHUNK_SIZE = 2 * Math.max(...PAGE_SIZE_OPTIONS);

export const monthlySalesOptions = toOptions([
  "RD$10,000 - RD$500,000",
  "RD$500,000 - RD$5,000,000",
  "RD$5,000,000 - RD$15,000,000",
  "Más de RD$15,000,000",
]);

export const emptyCatalogOptions: SelectOption[] = [];
export const accountTypeOptions = toOptions(["Ahorros", "Corriente"]);
export const bankOptions = emptyCatalogOptions;
export const businessTypeOptions = emptyCatalogOptions;
export const municipalityOptions = emptyCatalogOptions;
export const neighborhoodOptions = emptyCatalogOptions;
export const provinceOptions = emptyCatalogOptions;
export const sectorOptions = emptyCatalogOptions;
export const nationalityOptions = mapCountriesToNationalityOptions(countries);
export const creationStatusOptions = toOptions(Object.values(STATUS_LABELS));
export const connectivityOptions = toOptions(["Data SIM", "Wifi"]);
export const yesNoOptions = toOptions(["Sí", "No"]);

export const legalDocuments: LegalDocument[] = [
  { field: "rncRegistration", label: "Acta de inscripción del RNC", required: false },
  { field: "merchantRegistry", label: "Registro mercantil vigente", required: false },
  {
    field: "managerDesignation",
    label: "Acta que designa al gerente o representante",
    required: false,
  },
  { field: "socialStatutes", label: "Estatutos sociales", required: false },
];

export const initialAssistedAffiliationForm: AssistedAffiliationForm = {
  documentNumber: "",
  legalName: "",
  commercialName: "",
  monthlySales: "",
  businessType: "",
  representativeNames: "",
  representativeLastNames: "",
  representativeEmail: "",
  representativePhone: "",
  isForeignRepresentative: false,
  identityNumber: "",
  passportNumber: "",
  nationality: "",
  province: "",
  municipality: "",
  sector: "",
  neighborhood: "",
  streetAndNumber: "",
  reference: "",
  bank: "",
  accountNumber: "",
  accountType: "",
  executiveCode: "",
  executiveEmail: "",
  cashManagementEmail: "",
  commercialRepresentative: "",
  assignedCommercial: "Sin asignar",
  creationStatus: STATUS_LABELS.DRAFT,
  internalComments: "",
  billetSmart: true,
  terminalBillet: false,
  paymentLink: false,
  billetap: false,
  tapRate: "",
  tapQuantity: "",
  connectivityType: "",
  tipEnabled: "",
};

export const initialAssistedAffiliationFiles: AssistedAffiliationFiles = {
  frontIdentification: null,
  backIdentification: null,
  passportPhoto: null,
  accountEvidence: null,
};

export const initialLegalDocumentFiles: LegalDocumentFiles = {
  rncRegistration: [],
  merchantRegistry: [],
  managerDesignation: [],
  socialStatutes: [],
};

export const requiredAssistedAffiliationFields: AssistedAffiliationFieldName[] = [
  "documentNumber",
  "commercialName",
  "representativeNames",
  "representativeLastNames",
  "representativeEmail",
  "representativePhone",
  "billetSmart",
  "commercialRepresentative",
  "creationStatus",
];

export const DEFAULT_LEGAL_NAME = "Ferretería XYZ, SRL";

export const RNC_VALIDATION_SUCCESS_LINES = [
  "RNC válido ante DGII",
  "No existe en Billet",
  "No existe en Cardnet",
  "No existen solicitudes en curso",
];

// --- Alerta de cambios en validaciones ---

export const CLEAN_VALIDATIONS: AssistedAffiliationValidations = {
  valid_DGI: true,
  is_cardnet_client: false,
  exists_in_billet: false,
};

export const VALIDATION_CHANGES_MODAL = {
  title: 'Se detectaron cambios en la información del comercio',
  description: 'Al validar nuevamente la solicitud, encontramos cambios respecto a la información '
    + 'registrada anteriormente. Revise los resultados antes de continuar con la edición.',
  cancelLabel: 'Cancelar',
  confirmLabel: 'Revisar solicitud',
  acknowledgeLabel: 'Entendido',
  closeLabel: 'Cerrar',
};

interface ValidationSourceCopy {
  title: string;
  currentRowLabel: string;
  currentValue: (value: boolean) => string;
  // Valor del booleano que representa una situación desfavorable para la solicitud.
  adverseValue: boolean;
}

export const VALIDATION_SOURCES: Record<ValidationSource, ValidationSourceCopy> = {
  DGII: {
    title: 'DGII',
    currentRowLabel: 'Estado actual:',
    currentValue: (isValid) => (isValid ? 'Activo' : 'Suspendido'),
    adverseValue: false,
  },
  BILLET: {
    title: 'Billet',
    currentRowLabel: 'Validación actual:',
    currentValue: (exists) => (exists
      ? 'El comercio ya se encuentra registrado'
      : 'El comercio ya no se encuentra registrado'),
    adverseValue: true,
  },
  CARDNET: {
    title: 'Cardnet',
    currentRowLabel: 'Validación actual:',
    currentValue: (isClient) => (isClient
      ? 'El comercio ya existe en Cardnet'
      : 'El comercio ya no existe en Cardnet'),
    adverseValue: true,
  },
};

// Orden en el que el diseño apila las tarjetas, con el campo del backend que alimenta a cada una.
const VALIDATION_FIELDS: [ValidationSource, keyof AssistedAffiliationValidations][] = [
  ['DGII', 'valid_DGI'],
  ['BILLET', 'exists_in_billet'],
  ['CARDNET', 'is_cardnet_client'],
];

// La modal lista únicamente las validaciones que cambiaron. Si el arreglo sale vacío no hay nada
// que alertar y la modal no debe abrirse.
export const buildValidationChanges = (
  previous: AssistedAffiliationValidations,
  current: AssistedAffiliationValidations,
): ValidationChange[] => VALIDATION_FIELDS
  .filter(([, field]) => previous[field] !== current[field])
  .map(([source, field]) => {
    const copy = VALIDATION_SOURCES[source];
    return {
      source,
      currentLabel: copy.currentValue(current[field]),
      isAdverse: current[field] === copy.adverseValue,
    };
  });
